import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCallStateHooks, Video } from "@stream-io/video-react-sdk";
import { UserAvatar } from "@/features/chat/components/UserAvatar";
import {
  LocalSharePreview,
  ScreenShareIcon,
} from "@/features/call/components/ScreenShareIndicator";
import {
  SCREEN_SHARE_TRACK_TYPE,
  useLocalScreenShare,
} from "@/features/call/hooks/useScreenShare";

interface ScreenShareLayoutProps {
  raisedHands: Set<string>;
}

export const ScreenShareLayout = ({ raisedHands }: ScreenShareLayoutProps) => {
  const { t } = useTranslation();
  const { useRemoteParticipants, useLocalParticipant } = useCallStateHooks();
  const remoteParticipants = useRemoteParticipants();
  const localParticipant = useLocalParticipant();
  const { mediaStream, sourceLabel, stop: stopSharing } = useLocalScreenShare();
  const [showFilmstrip, setShowFilmstrip] = useState(true);
  const [showPreview, setShowPreview] = useState(false);

  const allParticipants = localParticipant
    ? [localParticipant, ...remoteParticipants]
    : remoteParticipants;

  // Find the participant sharing their screen
  const presenter = allParticipants.find((p) =>
    p.publishedTracks?.includes(SCREEN_SHARE_TRACK_TYPE),
  );
  const isLocalPresenter =
    !!presenter && presenter.sessionId === localParticipant?.sessionId;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Main: screen share */}
      <div className="flex-1 bg-slate-900 rounded-xl overflow-hidden m-2 min-h-0 relative">
        {isLocalPresenter ? (
          // Local presenter: the captured feed is not shown at full size — that
          // is what causes the infinite-mirror when "Entire screen" is picked.
          // Instead, name the exact surface being shared and offer an opt-in
          // thumbnail so the sharer can confirm what everyone else sees.
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-slate-400 px-4">
            <ScreenShareIcon className="w-10 h-10 text-indigo-400" />
            <p className="text-white text-sm font-medium text-center">
              {t("call.you_are_sharing", { source: sourceLabel })}
            </p>
            <p className="text-slate-500 text-xs">{t("call.others_can_see")}</p>
            {showPreview && <LocalSharePreview stream={mediaStream} />}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowPreview((v) => !v)}
                className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
              >
                {showPreview ? t("call.hide_preview") : t("call.show_preview")}
              </button>
              <button
                onClick={stopSharing}
                className="px-4 py-2 rounded-full bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors"
              >
                {t("call.stop_sharing")}
              </button>
            </div>
          </div>
        ) : presenter ? (
          // The Stream SDK's global CSS sets `.str-video__video { object-fit: cover }`
          // which crops the screen share. Override with !important on the inner <video>
          // so the full screen content is visible without zooming or clipping.
          <Video
            participant={presenter}
            trackType="screenShareTrack"
            className="w-full h-full [&_video]:!object-contain [&_video]:!w-full [&_video]:!h-full"
            VideoPlaceholder={() => (
              <div className="w-full h-full flex items-center justify-center text-slate-400">
                <p>{t("call.waiting_for_screen_share")}</p>
              </div>
            )}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-400">
            <p>{t("call.screen_share_ended")}</p>
          </div>
        )}

        {/* Who is presenting — remote viewers can't see the surface kind, so
            name the presenter instead */}
        {presenter && !isLocalPresenter && (
          <div className="absolute top-2 left-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-black/50 text-white text-xs backdrop-blur-sm max-w-[50%]">
            <ScreenShareIcon className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">
              {t("call.is_presenting", {
                name: presenter.name || presenter.userId,
              })}
            </span>
          </div>
        )}

        {/* Toggle participants button — top-right corner of the screen share area */}
        <button
          onClick={() => setShowFilmstrip((v) => !v)}
          className="absolute top-2 right-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-black/50 text-white text-xs hover:bg-black/70 transition-colors backdrop-blur-sm"
          title={showFilmstrip ? t("call.hide") : t("call.show_participants")}
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          {showFilmstrip ? t("call.hide") : t("call.show_participants")}
        </button>
      </div>

      {/* Bottom filmstrip: participant thumbnails */}
      {showFilmstrip && (
        <div className="h-20 flex gap-2 px-2 pb-2 overflow-x-auto flex-shrink-0">
          {allParticipants.map((p) => {
            const hasRaisedHand = raisedHands.has(p.userId);
            return (
              <div
                key={p.sessionId}
                className={`w-24 h-full rounded-lg overflow-hidden bg-slate-800 flex-shrink-0 relative ${
                  presenter?.sessionId === p.sessionId
                    ? "ring-2 ring-indigo-500"
                    : ""
                }`}
              >
                {p.publishedTracks?.includes(2) ? (
                  <Video
                    participant={p}
                    trackType="videoTrack"
                    mirror={p.sessionId === localParticipant?.sessionId}
                    className="w-full h-full object-cover"
                    VideoPlaceholder={() => (
                      <FilmstripPlaceholder participant={p} />
                    )}
                  />
                ) : (
                  <FilmstripPlaceholder participant={p} />
                )}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-1 py-0.5">
                  <p className="text-white text-[10px] font-medium truncate">
                    {p.sessionId === localParticipant?.sessionId
                      ? t("call.you")
                      : p.name || p.userId}
                  </p>
                </div>
                {/* Raised hand indicator */}
                {hasRaisedHand && (
                  <div className="absolute top-1 right-1 w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center text-[11px]">
                    ✋
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const FilmstripPlaceholder = ({ participant }: { participant: any }) => (
  <div className="w-full h-full flex items-center justify-center bg-slate-800">
    <UserAvatar
      name={participant.name || participant.userId}
      id={participant.userId}
      image={participant.image}
      size={32}
    />
  </div>
);
